import { z } from 'zod';

/**
 * Phase 5: Required status checks. Backed by
 * `flow_review_check_definitions` (project-level config) and
 * `flow_review_checks` (per-review state). Conceptually the same as
 * GitHub status checks: a definition declares "the smoke run must
 * pass before merge", and each review materialises a row that
 * tracks state pending → running → passed/failed/skipped.
 */

export const CheckKindSchema = z.enum(['run', 'lint', 'custom_webhook']);
export type CheckKind = z.infer<typeof CheckKindSchema>;

export const CheckStateSchema = z.enum(['pending', 'running', 'passed', 'failed', 'skipped']);
export type CheckState = z.infer<typeof CheckStateSchema>;

export const FlowReviewCheckDefinitionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  kind: CheckKindSchema,
  required: z.boolean(),
  /** Free-form per-kind config (e.g. webhook URL, lint rules). */
  config: z.record(z.unknown()).default({}),
});
export type FlowReviewCheckDefinition = z.infer<typeof FlowReviewCheckDefinitionSchema>;

export const FlowReviewCheckSchema = z.object({
  id: z.string().uuid(),
  reviewId: z.string().uuid(),
  definitionId: z.string().uuid(),
  name: z.string(),
  kind: CheckKindSchema,
  required: z.boolean(),
  state: CheckStateSchema,
  /** External URL to drill into (run report, lint output, …). */
  resultUrl: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type FlowReviewCheck = z.infer<typeof FlowReviewCheckSchema>;

// ---- CRUD over definitions ----

export const CreateCheckDefinitionBodySchema = z.object({
  name: z.string().min(1).max(120),
  kind: CheckKindSchema,
  required: z.boolean().default(true),
  config: z.record(z.unknown()).default({}),
});
export type CreateCheckDefinitionBody = z.infer<typeof CreateCheckDefinitionBodySchema>;

export const UpdateCheckDefinitionBodySchema = CreateCheckDefinitionBodySchema.partial();
export type UpdateCheckDefinitionBody = z.infer<typeof UpdateCheckDefinitionBodySchema>;

export const ListCheckDefinitionsResponseSchema = z.object({
  definitions: z.array(FlowReviewCheckDefinitionSchema),
});
export type ListCheckDefinitionsResponse = z.infer<typeof ListCheckDefinitionsResponseSchema>;

export const ListReviewChecksResponseSchema = z.object({
  checks: z.array(FlowReviewCheckSchema),
});
export type ListReviewChecksResponse = z.infer<typeof ListReviewChecksResponseSchema>;

// ---- Webhook callback (custom_webhook kind) ----

export const WebhookCheckCallbackSchema = z.object({
  state: CheckStateSchema,
  resultUrl: z.string().url().optional(),
});
export type WebhookCheckCallback = z.infer<typeof WebhookCheckCallbackSchema>;
